import { MapPin, Search, Home } from 'lucide-react'
import { useEffect, useRef, useState, useCallback } from 'react'
import { Card } from '#app/components/ui/card'
import { Input } from '#app/components/ui/input'
import { googleMapsLoader } from '#app/utils/google-maps-loader'

interface AddressComponents {
  street?: string
  houseNumber?: string
  postalCode?: string
  city?: string
  country?: string
  formatted?: string
  lat?: number
  lng?: number
}

interface GoogleAddressSearchProps {
  apiKey: string
  onAddressSelect: (address: AddressComponents) => void
  defaultValue?: string
}

interface Suggestion {
  placeId: string
  text: string
  secondary: string
  types: string[]
}

declare global {
  interface Window {
    google?: any
    initGoogleMaps?: () => void
  }
}

export function GoogleAddressSearch({ apiKey, onAddressSelect, defaultValue = '' }: GoogleAddressSearchProps) {
  const [inputValue, setInputValue] = useState(defaultValue)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [streetViewUrl, setStreetViewUrl] = useState<string | null>(null)
  const [selectedAddress, setSelectedAddress] = useState<string>(defaultValue)
  const [hasError, setHasError] = useState(false)
  const [needsHouseNumber, setNeedsHouseNumber] = useState(false)
  const sessionTokenRef = useRef<any>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!input || input.length < 3 || !window.google?.maps?.places) {
      setSuggestions([])
      return
    }

    try {
      const { AutocompleteSuggestion, AutocompleteSessionToken } =
        window.google.maps.places
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new AutocompleteSessionToken()
      }

      const { suggestions: results } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: sessionTokenRef.current,
          includedRegionCodes: ['nl'],
          // Include `route` so a bare street name ("Begoniastraat") returns
          // suggestions before the user has typed a house number — Google
          // classifies street-only matches as `route`, and `street_address`
          // / `premise` only match once a number is present.
          includedPrimaryTypes: ['route', 'street_address', 'premise', 'subpremise'],
          language: 'nl',
        })

      const mapped: Suggestion[] = results
        .map((s: any) => {
          const prediction = s.placePrediction
          if (!prediction) return null
          return {
            placeId: prediction.placeId,
            text: prediction.mainText?.toString() ?? prediction.text?.toString() ?? '',
            secondary: prediction.secondaryText?.toString() ?? '',
            types: (prediction.types ?? []) as string[],
          }
        })
        .filter(Boolean) as Suggestion[]

      setSuggestions(mapped)
      setShowSuggestions(mapped.length > 0)
    } catch (error) {
      console.error('Failed to fetch place suggestions:', error)
      setSuggestions([])
    }
  }, [])

  const promptForHouseNumber = useCallback(
    (streetName: string) => {
      const prefilled = `${streetName} `
      setInputValue(prefilled)
      setSelectedAddress('')
      setStreetViewUrl(null)
      setNeedsHouseNumber(true)
      onAddressSelect({})
      // Refocus and refetch suggestions for the augmented input so the user
      // sees precise addresses appear as they type the number.
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        inputRef.current?.setSelectionRange(prefilled.length, prefilled.length)
      })
      void fetchSuggestions(prefilled)
    },
    [fetchSuggestions, onAddressSelect],
  )

  const handleSelect = useCallback(
    async (suggestion: Suggestion) => {
      // If the prediction is a street-only match, don't burn a Place Details
      // call — we can't get a precise address from it. Prefill the input with
      // the street name and a trailing space so the user types the number,
      // and reopen the dropdown with refined suggestions.
      const isRouteOnly =
        suggestion.types.includes('route') &&
        !suggestion.types.some((t) =>
          ['street_address', 'premise', 'subpremise'].includes(t),
        )
      if (isRouteOnly) {
        promptForHouseNumber(suggestion.text)
        return
      }

      if (!window.google?.maps?.places?.Place) return
      try {
        const { Place } = window.google.maps.places
        const place = new Place({ id: suggestion.placeId })
        await place.fetchFields({
          fields: ['addressComponents', 'location', 'formattedAddress', 'displayName'],
        })

        const components: AddressComponents = {
          formatted: place.formattedAddress ?? undefined,
          lat: place.location?.lat(),
          lng: place.location?.lng(),
        }

        for (const component of place.addressComponents ?? []) {
          const types: string[] = component.types ?? []
          if (types.includes('route')) {
            components.street = component.longText ?? component.shortText
          } else if (types.includes('street_number')) {
            components.houseNumber = component.longText ?? component.shortText
          } else if (types.includes('postal_code')) {
            components.postalCode = component.longText ?? component.shortText
          } else if (types.includes('locality')) {
            components.city = component.longText ?? component.shortText
          } else if (types.includes('country')) {
            components.country = component.longText ?? component.shortText
          }
        }

        // Defensive: even non-route suggestions can occasionally come back
        // without a street_number (e.g. interpolated rural addresses). The
        // app requires a precise address, so prompt for the house number.
        if (!components.houseNumber && components.street) {
          promptForHouseNumber(components.street)
          return
        }

        const display = place.formattedAddress ?? suggestion.text
        setSelectedAddress(display)
        setInputValue(display)
        setShowSuggestions(false)
        setNeedsHouseNumber(false)
        sessionTokenRef.current = null

        if (components.lat && components.lng) {
          setStreetViewUrl(
            `https://maps.googleapis.com/maps/api/streetview?size=640x480&location=${components.lat},${components.lng}&fov=80&pitch=0&key=${apiKey}`,
          )
        }

        onAddressSelect(components)
      } catch (error) {
        console.error('Failed to fetch place details:', error)
      }
    },
    [apiKey, onAddressSelect, promptForHouseNumber],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    // Any further typing invalidates a previous selection — the user is
    // editing again, so clear the captured address until they re-select.
    if (selectedAddress) {
      setSelectedAddress('')
      setStreetViewUrl(null)
      onAddressSelect({})
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => void fetchSuggestions(value), 250)
  }

  useEffect(() => {
    if (!apiKey || apiKey === '' || apiKey === 'your-google-maps-api-key') {
      console.warn('Google Maps API key not configured')
      setHasError(true)
      setIsLoading(false)
      return
    }

    googleMapsLoader.setApiKey(apiKey)

    googleMapsLoader
      .load({ libraries: ['places'] })
      .then(async () => {
        // With loading=async the places library is loaded but the modular
        // classes (AutocompleteSuggestion, Place) are only attached after
        // importLibrary resolves.
        if (!window.google?.maps?.importLibrary) {
          throw new Error('Google Maps importLibrary not available')
        }
        const placesLib = await window.google.maps.importLibrary('places')
        if (!placesLib?.AutocompleteSuggestion || !placesLib?.Place) {
          throw new Error(
            'Places (New) API classes not available — check that "Places API (New)" is enabled in Google Cloud Console and that the API key allows the localhost referrer.',
          )
        }
        setIsLoading(false)
        setHasError(false)
      })
      .catch((error) => {
        console.error('Failed to load Google Maps:', error)
        setHasError(true)
        setIsLoading(false)
      })
  }, [apiKey])

  // Close dropdown on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  return (
    <div className="space-y-4" ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Zoek je adres..."
          className="pl-10 h-12 text-lg"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          disabled={isLoading || hasError}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full" />
          </div>
        )}

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
            {suggestions.map((s) => (
              <button
                key={s.placeId}
                type="button"
                className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
                onMouseDown={(e) => {
                  e.preventDefault()
                  void handleSelect(s)
                }}
              >
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.text}</p>
                    {s.secondary && (
                      <p className="text-xs text-gray-500 truncate">{s.secondary}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {streetViewUrl && selectedAddress && (
        <Card className="overflow-hidden">
          <div className="relative h-64 bg-gray-100">
            <img
              src={streetViewUrl}
              alt="Street view"
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                const fallbackUrl = streetViewUrl
                  .replace('streetview', 'staticmap')
                  .replace('&fov=80&pitch=0', '&zoom=17&maptype=roadmap')
                target.src = fallbackUrl
              }}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
              <div className="flex items-center text-white">
                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                <p className="text-sm font-medium truncate">{selectedAddress}</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {needsHouseNumber && !selectedAddress && (
        <div className="flex items-center justify-center py-3 text-blue-600 bg-blue-50 rounded-md">
          <p className="text-sm">Vul je huisnummer in om je adres te bevestigen</p>
        </div>
      )}

      {!selectedAddress && !isLoading && !hasError && !needsHouseNumber && (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Home className="h-5 w-5 mr-2" />
          <p>Begin met typen om je adres te vinden</p>
        </div>
      )}

      {hasError && (
        <div className="flex items-center justify-center py-8 text-yellow-600">
          <p className="text-sm">Adres zoeken is tijdelijk niet beschikbaar. Vul je adres handmatig in.</p>
        </div>
      )}
    </div>
  )
}
